import React, { useEffect, useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { FilterChip } from '../FilterChip';
export interface FilterOption {
  id: string;
  label: string;
  value: string;
}
export interface Filter {
  id: string;
  label: string;
  options: FilterOption[];
  selectedValue?: string;
  selectedValues?: string[];
  multiSelect?: boolean;
  type?: 'dropdown' | 'search';
  isOpen?: boolean;
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
}
export interface TableFilterProps {
  filters?: Filter[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;
  onFilterRemove?: (filterId: string) => void;
  onAddFilter?: (filterId: string) => void;
  onToggleDropdown?: (filterId: string) => void;
  onCustomDateChange?: (filterId: string, startDate: string, endDate: string) => void;
  addedFilterIds?: Set<string>;
  'data-id'?: string;
  // New props for granular rendering
  renderOnlyButton?: boolean;
  renderOnlyChips?: boolean;
}
export function TableFilter({
  filters = [],
  onFilterChange,
  onFilterRemove,
  onAddFilter,
  onToggleDropdown,
  onCustomDateChange,
  addedFilterIds = new Set(),
  'data-id': dataId,
  renderOnlyButton,
  renderOnlyChips
}: TableFilterProps) {
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const addFilterRef = useRef<HTMLDivElement>(null);
  // Close add filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addFilterRef.current && !addFilterRef.current.contains(event.target as Node)) {
        setIsAddFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const activeFilters = filters.filter(f => addedFilterIds.has(f.id));
  const availableFilters = filters.filter(f => !addedFilterIds.has(f.id));
  const renderAddButton = () => {
    if (availableFilters.length === 0) return null;
    return <div className="relative" ref={addFilterRef} data-id="element-3510">
        <Button variant="outline" size="md" onClick={() => setIsAddFilterOpen(!isAddFilterOpen)} iconLeft={<Plus className="w-4 h-4" data-id="element-3512" />} className={isAddFilterOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''} data-id="element-3511">
          Add Filter
        </Button>

        {isAddFilterOpen && <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-[1000] overflow-hidden animate-in fade-in zoom-in-95 duration-100" data-id="element-3513">
            <div className="p-1" data-id="element-3514">
              {availableFilters.map(filter => <button key={filter.id} onClick={() => {
            onAddFilter?.(filter.id);
            setIsAddFilterOpen(false);
          }} className="w-full text-left px-3 py-2.5 text-[14px] font-display text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-3515">
                  {filter.label}
                </button>)}
            </div>
          </div>}
      </div>;
  };
  const renderChips = () => {
    return activeFilters.map(filter => {
      return <FilterChip key={filter.id} filter={filter} onSelect={value => onFilterChange?.(filter.id, value)} onRemove={() => onFilterRemove?.(filter.id)} onCustomDateChange={onCustomDateChange} data-id="element-3516" />;
    });
  };
  if (renderOnlyButton) {
    return <div className="relative" data-id="element-3517">{renderAddButton()}</div>;
  }
  if (renderOnlyChips) {
    return <div className="flex items-center gap-3 flex-wrap" data-id="element-3518">{renderChips()}</div>;
  }
  return <div className="flex flex-col gap-4 w-full" data-id="element-3519">
      {/* Top Row: Add Filter Button */}
      <div className="flex items-center" data-id="element-3520">{renderAddButton()}</div>

      {/* Bottom Row: Active Filter Chips */}
      {activeFilters.length > 0 && <div className="flex items-center gap-3 flex-wrap" data-id="element-3521">{renderChips()}</div>}
    </div>;
}